// Onboarding.tsx — everyone the onboarding bot has met who needs the team, every
// potential lead still talking to it, and every approved client — a card each,
// read at a glance as four approval checks, with what their wallet holds and a
// chat button for every person.
//
// The operator, 9 Oct 2026, on the page before this one: "size of the fonts,
// using cards, colors where applicable, reads as sections like SoF Passed, KYC
// passed, Residency/nationality passed/flagged/pennding, tg card with Tg blue
// color- something like that- a total makeover of the screens so that it reads
// more intuative and easy to spot/assess/check". 12 Oct, on the mockup: "Build it
// as shown", and "wallet funds should also be listed on that screen itself".
// Before that, 8 Oct: "organised format, so that it reads clean rather than
// clutter"; 7 Oct: "all should have a Telegram button which redirects to their
// chat"; for a Telegram account with no username, "Bot sends you the button";
// "if not on telegram then direct button to their whatsapp".
//
// THREE TABS, the counts on them (?view=): NEEDS YOU — the four queues (stuck,
// waiting for a decision, approved and not contacted, escalated); POTENTIAL
// (?view=potential) — still in the chat with the bot, said what they want, not
// refused, nothing with the team yet; CLIENTS (?view=approved) — every approved
// client, then the verified records from before. Above them one sentence: how
// many people need you, and how long the oldest has waited.
//
// A CARD A PERSON, the same on every tab: who they are, their standing and the
// chat button; the trade big, with what their wallet holds under it; and the
// worker's four checks — KYC, Residency, Funds, Wallet — each Passed, Pending or
// Flagged with its one line of why. From xl the cards' columns line up, so a red
// can be spotted down the page; on a phone the checks are two by two. A refusal,
// a first trade over the cap — a warning no check says — is a thin strip across
// the card's top. ?lead= opens a person's whole record beside the list
// (screens/OnboardingRecord.tsx).
//
// THE CHAT BUTTON, on every card, named and coloured for the app it opens
// (Telegram blue, WhatsApp green): a link opens straight away — inside the Mini
// App through Telegram's own openers, in a browser in a new tab with no opener. A
// Telegram account with no username has no link a page can open, so the button
// asks the bot (POST /api/onboarding/chat-link), which sends the operator a
// one-tap button in Telegram. No way to reach them: "Website only".
//
// THE SEARCH, above the tabs (the operator, 14 Oct 2026: "how can I search via
// mobile number/name/TG Id/email?"; his choice: one box, every tab at once, on
// the server, never logged). 300 ms after the last key, two characters at least,
// the BFF is asked by POST (POST /api/onboarding/search) — what is typed never
// goes in the address or a log, and lives in this screen alone. While the box
// holds text its results stand where the tabs were: Needs you · Potential ·
// Clients · Not on a list, each with its count, each person on their tab's own
// card with what they were found by. × or Esc brings the tabs back.
//
// WHAT THE WALLET HOLDS NOW (14 Oct: "wallet live balance i need to see"): the
// card's wallet line is the worker's latest reading of the chain when it has one
// — "Wallet: 52,000 USDT · 40 TRX · 3 min ago", counted to the read — else the
// line its check gave. WAITING ON THEM (14 Oct: the bot "asks for pending
// information until we get everything"): a lead or potential lead the chat waits
// on says what for across the card's foot, and until when the bot can write to
// them — in amber, the team's to do, once it cannot.
//
// THE WORKER DECIDES, THE SCREEN SHOWS. The onboarding worker builds the lists,
// orders them, judges every person and writes every check; the BFF passes exactly
// the contract's fields (bff/src/routes/leads.ts). Nothing here is re-sorted, and
// nothing is decided: Approve / Reject, Mark handled and Mark contacted stay on
// the cards in the Telegram ops group. A worker older than the checks sends none:
// a card then shows none, never a verdict made up for it.
//
// THE RULES every status line on this dashboard keeps:
//   1. A count that could not be read says so — never a 0; records the worker
//      could not build are said, and then no count reads as complete.
//   2. When before what — every age is counted to the moment the list was read
//      (fetched_at, the BFF's clock), never the viewer's clock; a read more than
//      ten minutes old says so; a page read on and an open record are reads of
//      their own.
//   3. Green is earned — "Nobody needs you right now" only after a good, fresh
//      read whose counts are all zero, with nothing the worker could not read.
//   4. A failed read never shows an empty list as good, and keeps what it had,
//      said to be the earlier read.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import type { BotRefusal, LeadChannel, LeadLabelKey, LeadQueue, OnboardingLeads } from '../lib/api';
import { fmtAmount, fmtDate, fmtDay } from '../lib/format';
import { ScreenHeader } from '../components/ScreenHeader';
import { Pill } from '../components/Pill';
import { EmptyState, StateLine, type StateTone } from '../components/kit';
import {
  CardButton, ChatButton, CheckChips, Parts, READ_EVERY_MS, Reason, TradeBig, WaitingLine, WalletLineView,
  ago, failLine, failMore, failSaid, failWords, readOf, sentenceOf, targetOf, type Read,
} from '../components/OnboardingParts';
import { RecordSheet, type RecordTab, type RowSummary } from './OnboardingRecord';
import {
  CHECKS, FOUND_WORD, liveLine, noWalletYet, onboardingApi, saleCoin, waitingWords,
  type Assessment, type Check, type Claim, type ClientRow, type ClientsList, type ElsewherePerson, type Facts, type FoundBy, type LeadRow,
  type LeadsList, type ProspectRow, type ProspectsList, type ReadAs, type SearchResult, type WaitingOn, type WalletCheck, type WalletLine,
  type WalletLive,
} from '../lib/onboarding';

// The chat button's openers, for the screen's tests (tests/leads-screen): they live with the parts the record shares.
export { openChat, chatLinkNote } from '../components/OnboardingParts';

/** And the screen looks at its own clock this often, so a list that has stopped refreshing turns stale on screen. */
const TICK_MS = 30_000;

type View = 'needs' | 'potential' | 'clients';
type Chip = 'stuck' | 'waiting' | 'approved' | 'escalated';
type Count = keyof OnboardingLeads['counts'];
type SetParams = (set: Record<string, string | null>) => void;
type Tone = 'bad' | 'warn' | null;
type Strip = { text: string; tone: 'bad' | 'warn' };

/** The four queues in the worker's order: the address's word, the standing's stage, the queue's name, its count, and
 *  its own labels — whose tone (the worker's thresholds) colours the standing. */
const QUEUES: ReadonlyArray<{ queue: LeadQueue; chip: Chip; stage: string; name: string; items: string; count: Count; own: LeadLabelKey[] }> = [
  { queue: 'stuck_no_card', chip: 'stuck', stage: 'Stuck', name: 'Stuck — the bot could not send the card', items: 'stuck applications', count: 'stuck', own: ['stuck'] },
  { queue: 'awaiting_decision', chip: 'waiting', stage: 'Waiting', name: 'Waiting for a decision', items: 'waiting cards', count: 'awaitingDecision', own: ['waiting'] },
  { queue: 'approved_not_contacted', chip: 'approved', stage: 'Approved · not contacted', name: 'Approved — not contacted', items: 'approved clients', count: 'approvedNotContacted',
    own: ['approved_not_contacted', 'contact_not_recorded'] },
  { queue: 'escalation_open', chip: 'escalated', stage: 'Escalated', name: 'Escalations open', items: 'escalations', count: 'escalationsOpen', own: ['escalated'] },
];
type QueueInfo = (typeof QUEUES)[number];

const CHANNEL_WORD: Record<LeadChannel, string> = { business: 'Business', web: 'Web', 'bot-dm': 'Bot DM' };
/** The intake's questions as words (onboarding-agent src/onboarding/intake.ts): what a potential lead still has to answer. */
const MISSING_WORD: Record<string, string> = {
  side: 'buy or sell', asset: 'which crypto', size: 'size', frequency: 'how often', settlement_out: 'how they want to be paid',
  beneficiary: 'who receives the payment', full_name: 'full name', passport_country: 'nationality', jurisdiction: 'where they live',
  uae_resident: 'UAE residence', physically_in_uae: 'whether they are in the UAE', on_own_behalf: 'whether it is their own money',
  wallet_address: 'wallet', wallet_chain: 'wallet network', wallet_origin: 'where the wallet is from', source_of_funds: 'source of funds',
  purpose: 'purpose', referred_by: 'who referred them',
};
/** The bot's reasons for refusing someone, in words (onboarding-agent's codes). */
const REFUSAL_WORD: Record<string, string> = {
  below_minimum: 'below the minimum', residency: 'not a UAE resident in Dubai', tourist_buy: 'a tourist buying',
  tourist_not_tier1: 'a tourist outside the exceptions', unsupported_request: 'a request the desk does not take',
  sanctioned_passport: 'a sanctioned passport', third_party: "someone else's or a company's money", anonymous: 'refused verification',
  exceeds_first_trade_max: 'a first buy over the cap', previously_declined: 'declined before', other: 'other',
};
/** A code as words: the map's own entry, else `otherwise(code)`. Own entries only — a code is the worker's or the
 *  customer's text, and "__proto__" or "constructor" must never find what every object inherits. */
const own = (map: Record<string, string>, code: string, otherwise: (code: string) => string): string =>
  (Object.hasOwn(map, code) ? map[code] ?? otherwise(code) : otherwise(code));
const missingWord = (id: string) => own(MISSING_WORD, id, (c) => c);
const refusalWord = (code: string) => own(REFUSAL_WORD, code, () => 'other');

const plural = (n: number, one: string, many: string) => `${count(n)} ${n === 1 ? one : many}`;
/** A count in the books' own grouping (lib/format: international, 1,234). */
const count = (n: number) => fmtAmount(n, undefined, 0);
const dayWords = (n: number) => (n === 0 ? 'under a day' : plural(n, 'day', 'days'));

/** How long a lead has sat in its queue, counted to the moment the list was READ — not to the viewer's clock
 *  (rule 2): "6d" from a day on, in whole 24-hour days (the worker's own rule, so the age never disagrees with its
 *  label), "5h" under a day, "<1h" under an hour. The worker's own figure stands in only when `since` could not be read. */
function shortAge(l: LeadRow, readMs: number): string {
  const since = l.since ? Date.parse(l.since) : NaN;
  if (!Number.isFinite(since) || !Number.isFinite(readMs)) return l.ageDays !== null ? `${l.ageDays}d` : 'age not known';
  const ms = Math.max(0, readMs - since);
  if (ms < 3_600_000) return '<1h';
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h`;
  return `${Math.floor(ms / 86_400_000)}d`;
}

/** How long before the read an instant was — "5 min ago" — counted to the BFF's clock, never the viewer's (rule 2): the
 *  same words a minute later or a day later. The date and time itself when the read's own time could not be read. */
function atRead(iso: string, readMs: number): string {
  const t = Date.parse(iso);
  return Number.isFinite(t) && Number.isFinite(readMs) ? ago(Math.max(0, readMs - t)) : fmtDate(iso);
}

/** Renders the screen again every so often: "Updated 2 min ago" keeps counting, and a list that has stopped
 *  refreshing — a dropped connection, a laptop that slept — turns stale on screen without a reload. */
function useTick(everyMs: number): void {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), everyMs);
    return () => clearInterval(t);
  }, [everyMs]);
}

// ── What a card says ─────────────────────────────────────────────────────────

/** The lead's stage and how long it has been there — "Waiting · 56d", "Approved · not contacted · 3d" — and how often
 *  the customer wrote since ("wrote 2×"); an escalation's reason under it. Coloured by the worker's own labels for
 *  these, at its thresholds: an old wait, an approval nobody contacted, every stuck application, a customer who keeps
 *  writing. */
function standing(l: LeadRow, readMs: number): { text: string; tone: Tone; why: string | null } {
  const q = QUEUES.find((x) => x.queue === l.queue)!;
  const notRecorded = l.queue === 'approved_not_contacted' && l.labels.some((x) => x.key === 'contact_not_recorded');
  const stage = notRecorded ? 'Approved · contact not recorded' : q.stage;
  const wrote = l.chasedTimes ? ` · wrote ${count(l.chasedTimes)}×` : '';
  const tones = l.labels.filter((x) => q.own.includes(x.key) || x.key === 'chased').map((x) => x.tone);
  // An approval nobody contacted is a problem whatever the worker's age threshold says: never plain.
  const tone: Tone = tones.includes('bad') ? 'bad' : tones.includes('warn') || l.queue === 'approved_not_contacted' ? 'warn' : null;
  const why = l.queue === 'escalation_open' && l.escalationReason ? l.escalationReason.replace(/_/g, ' ') : null;
  return { text: `${stage} · ${shortAge(l, readMs)}${wrote}`, tone, why };
}

/** Whether a check on the card says a warning itself: its verdict, and its line of why carrying the worker's own
 *  words for it (onboarding-agent src/admin/assess.ts). A check says one cause at a time — a wallet Arkham flags is
 *  "Arkham flags it — …", which says nothing of its risk band — so a check standing on the card is not enough: only
 *  its words are. */
const checkSays = (c: Check | null | undefined, status: Check['status'], words: string | null): boolean =>
  !!c && c.status === status && (words === null || (c.reason ?? '').includes(words));

/** Whether the card already says what the worker's label says, in the same sense — the standing its queue and its
 *  "wrote N×", the who line "Returning", the chat button "Website only", and the check whose line of why says the
 *  same thing ("Not a UAE resident" in Residency, "On a sanctions list" or "Risk severe (97)" in Wallet, "1 of 2 ID
 *  images" in KYC; a pending wallet check is a wallet with no score yet). Only then is the label left off the card's
 *  strip: each fact once. A label whose fact the card does not show — a first trade over the cap, a wallet another
 *  customer used, answers changed after the card, a severe risk behind an Arkham flag — is the only place its warning
 *  is said, and stays. */
function saidOnCard(l: LeadRow, key: LeadLabelKey): boolean {
  const q = QUEUES.find((x) => x.queue === l.queue);
  if (q?.own.includes(key)) return true;
  const a = l.assessment;
  switch (key) {
    case 'chased':          return !!l.chasedTimes;
    case 'returning':       return true;
    case 'no_contact_path': return !l.contactUrl && !l.chatViaBot;
    case 'not_resident':    return checkSays(a?.residency, 'flagged', 'Not a UAE resident');
    case 'not_in_uae':      return checkSays(a?.residency, 'flagged', 'Not in the UAE now');
    case 'not_own_behalf':  return checkSays(a?.funds, 'flagged', 'Not their own money');
    case 'one_id_image':    return checkSays(a?.kyc, 'pending', '1 of 2 ID images');
    case 'sanctions':       return checkSays(a?.wallet, 'flagged', 'On a sanctions list');
    case 'wallet_severe':   return checkSays(a?.wallet, 'flagged', 'Risk severe');
    case 'wallet_high':     return checkSays(a?.wallet, 'flagged', 'Risk high');
    // A wallet check pending is one with no risk score: no wallet yet, not scored yet, not checked yet.
    case 'not_scored':      return checkSays(a?.wallet, 'pending', null);
    default:                return false;
  }
}

/** The worker's own warnings that the card does not say, the worst first — the card's strip, red when one is bad. */
function flagsOf(l: LeadRow): Strip | null {
  const flags = l.labels.filter((x) => !saidOnCard(l, x.key) && (x.tone === 'bad' || x.tone === 'warn'));
  if (flags.length === 0) return null;
  const bad = flags.filter((x) => x.tone === 'bad');
  return { text: [...bad, ...flags.filter((x) => x.tone === 'warn')].map((x) => x.text).join(' · '), tone: bad.length ? 'bad' : 'warn' };
}

/** The bot's refusal of an approved client since the approval — shown, never acted on: "Operator word is final"
 *  (operator, 7 Oct 2026). A compliance refusal in red, a decline in amber; one whose kind could not be read is said
 *  as either, in red — never the milder word for what may be a compliance refusal. Aged against the read (rule 2). */
function refusalStrip(r: BotRefusal | null, readMs: number): Strip | null {
  if (!r) return null;
  const what = r.compliance === true ? 'Refused by the bot' : r.compliance === false ? 'Declined by the bot' : 'Refused or declined by the bot';
  const when = r.at ? atRead(r.at, readMs) : 'when not known';
  const why = r.reasons === null ? 'reasons not known' : r.reasons.map(refusalWord).join(', ');
  return { text: `${what} ${when} — ${why}`, tone: r.compliance === false ? 'warn' : 'bad' };
}

/** What a card reads: who, their standing, what else qualifies them, their buttons, the trade with what the wallet
 *  holds, the four checks, a warning no check says — and, under it all, what the chat waits on them for. */
interface Person {
  key: string;
  name: string;
  /** Their @username, or where they came from, under the name. */
  handle: string | null;
  channel: LeadChannel | null;
  returning?: boolean;
  standing: { text: string; tone: Tone; why?: string | null; title?: string | null } | null;
  note?: ReactNode;
  /** The chat button, on a line of its own; under it the quieter links — the card in the ops group, the record. */
  chat: ReactNode;
  links: ReactNode;
  claim: Claim | null;
  facts: Facts | null;
  /** What the wallet holds — now, read from its chain, when there is a reading; else at its check. */
  wallet: WalletLine;
  assessment: Assessment | null;
  strip: Strip | null;
  /** "Waiting on them: …" — Needs you and Potential only. */
  waiting?: ReactNode;
  /** A search result: what the person was found by. */
  found?: FoundBy[];
  /** Opens the person's record — the whole card does (the operator, 14 Oct 2026: "open record button is not visible,
   *  make it clickable whole record"); none for a record with no chat here. */
  open?: () => void;
  /** This card's record is the one open beside the list. */
  isOpen?: boolean;
}

/** The whole card opens the record — but not a click on a control inside it (the chat, the card's link, a "more"), nor
 *  the end of a drag that selected text. The "Open record" button stays, for the keyboard and screen readers. */
export const cardClick = (open: () => void) => (e: { target: EventTarget | null }) => {
  const t = e.target as Element | null;
  if (t?.closest?.('a, button, input, textarea, select, label, summary, [role="button"], [data-no-open]')) return;
  if ((typeof window !== 'undefined' ? window.getSelection?.()?.toString() ?? '' : '').length > 0) return;
  open();
};
/** A card that opens a record: the pointer says so, and the one whose record is open is marked. */
const OPENS = 'cursor-pointer transition-colors hover:border-tg-link/60';

/** What a card's wallet line says: what the wallet holds now when there is a live reading (counted to the read),
 *  else what its check said — or why there is nothing. */
const cardWallet = (live: WalletLive | null | undefined, check: WalletCheck | null, claim: Claim | null, noWallet: boolean, readMs: number): WalletLine =>
  liveLine(live, check, saleCoin(claim), noWallet, readMs);

/** "Found by: phone number · name" — a search result's tag. */
function FoundTag({ found }: { found: FoundBy[] }) {
  if (found.length === 0) return null;
  return (
    <p className="text-meta" data-found="">
      <span className="inline-flex flex-wrap items-center gap-x-1 rounded-md bg-panel-2 px-2 py-0.5 text-tg-hint">
        Found by: <span className="text-tg-text"><Parts text={found.map((f) => FOUND_WORD[f]).join(' · ')} /></span>
      </span>
    </p>
  );
}

/** The name first: the one they gave, else their @username, else where they came from. Under it their @username
 *  where they have one — else where they came from — when the name is not it. */
function naming(name: string | null, who: string | null, channel: LeadChannel | null): { name: string; handle: string | null } {
  const handle = who?.startsWith('@') ? who : null;
  const first = name ?? handle ?? who ?? 'Not named';
  return { name: first, handle: name ? handle ?? (channel === 'web' ? 'Website' : channel ? 'Telegram' : null) : null };
}

/** The columns every card shares from xl, so the eye runs down one check: person · trade · the four checks. */
const ROW = 'xl:grid-cols-[minmax(0,15.5rem)_minmax(0,13.5rem)_repeat(4,minmax(0,1fr))]';

/** One person's card. */
function PersonCard({ p }: { p: Person }) {
  const tone = p.standing?.tone === 'bad' ? 'text-neg' : p.standing?.tone === 'warn' ? 'text-warn' : 'text-tg-hint';
  return (
    <li aria-label={p.name}>
      <article
        className={`card overflow-hidden p-0 ${p.open ? OPENS : ''} ${p.isOpen ? 'ring-2 ring-tg-link/70' : ''}`}
        onClick={p.open ? cardClick(p.open) : undefined}
        data-opens={p.open ? '' : undefined}
      >
        {p.strip && (
          <p className={`border-b px-4 py-1.5 text-body font-medium ${p.strip.tone === 'bad' ? 'border-neg/25 bg-neg-bg text-neg' : 'border-warn/25 bg-warn-bg text-warn'}`} data-strip={p.strip.tone}>
            {p.strip.text}
          </p>
        )}
        <div className={`grid gap-x-5 gap-y-3 px-4 py-4 ${ROW} xl:items-start`}>
          <div className="min-w-0 space-y-1">
            {/* A name wraps between its words; a username has none to wrap at, so it is cut short on one line — whole in
                its tooltip — rather than broken mid-name across two. */}
            <h3 className={`text-title font-semibold leading-tight ${p.name.startsWith('@') ? 'truncate' : 'break-words'}`} title={p.name}>{p.name}</h3>
            {(p.handle || p.channel || p.returning) && (
              <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-body text-tg-hint">
                {p.handle && <span className="min-w-0 max-w-full truncate" title={p.handle}>{p.handle}</span>}
                {p.channel && <Pill>{CHANNEL_WORD[p.channel]}</Pill>}
                {p.returning && <Pill>Returning</Pill>}
              </p>
            )}
            {p.found && <FoundTag found={p.found} />}
            {p.standing && (
              <p className={`text-body leading-snug ${tone}`} title={p.standing.title ?? undefined} data-standing={p.standing.tone ?? 'plain'}>
                <Parts text={p.standing.text} />
              </p>
            )}
            {/* Why, under the standing — an escalation's reason, the answers still to come: two lines, the rest on a tap. */}
            {p.standing?.why && <Reason text={p.standing.why} size="text-body text-tg-hint" />}
            {p.note}
            {/* The same two lines on every card: the chat, then the links — one rhythm down the page. */}
            <div className="space-y-1 pt-1.5" data-ways="">
              <div>{p.chat}</div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">{p.links}</div>
            </div>
          </div>
          <div className="min-w-0 space-y-1.5">
            <TradeBig c={p.claim} f={p.facts} />
            <WalletLineView line={p.wallet} />
          </div>
          <CheckChips a={p.assessment} />
        </div>
        {/* What the chat waits on them for, across the card's foot: plain while the bot can ask, amber when the team must. */}
        {p.waiting}
      </article>
    </li>
  );
}

/** A tab's cards — and, above them, when no card carries the four checks, why: a worker older than them. */
function PersonList({ label, people }: { label: string; people: Person[] }) {
  return (
    <>
      {people.length > 0 && people.every((p) => !p.assessment) && (
        <div data-no-checks="">
          <StateLine tone="neutral" more="The four checks — KYC, residency, funds and wallet — come with the bot's next update; until then no card shows them.">
            Checks come with the bot&apos;s next update.
          </StateLine>
        </div>
      )}
      <ol className="space-y-3" aria-label={label}>
        {people.map((p) => <PersonCard key={p.key} p={p} />)}
      </ol>
    </>
  );
}

/** The record button on a card: it opens the person's whole record beside the list, and closes it again. */
function RecordButton({ id, who, open, toggle }: { id: string; who: string; open: boolean; toggle: (id: string) => void }) {
  return (
    <button type="button" className="btn-link whitespace-nowrap text-body" aria-expanded={open}
      aria-label={`${open ? 'Close' : 'Open'} the record of ${who}`} onClick={() => toggle(id)}>
      {open ? 'Close record' : 'Open record'}
    </button>
  );
}

// ── The screen ───────────────────────────────────────────────────────────────

/** The screen. `initialQuery`: the search box's first text — the tests' way in, as a server render cannot type; the
 *  console opens it empty. What is typed lives in this screen alone: never the address, never a log. */
export function Onboarding({ initialQuery = '' }: { initialQuery?: string } = {}) {
  useTick(TICK_MS);
  const qc = useQueryClient();
  const [search, setSearch] = useSearchParams();
  const asked = search.get('view');
  // ?view=approved stays the clients' key: links already made to it still open them.
  const view: View = asked === 'approved' ? 'clients' : asked === 'potential' ? 'potential' : 'needs';
  // Only these are the screen's: whatever else the address carries stays.
  const setParams: SetParams = (set) => setSearch((prev) => {
    const next = new URLSearchParams(prev);
    for (const [k, v] of Object.entries(set)) if (v === null) next.delete(k); else next.set(k, v);
    return next;
  }, { replace: true });
  const openLead = search.get('lead');
  const recordTab: RecordTab = (['details', 'chat', 'history'] as const).find((t) => t === search.get('tab')) ?? 'details';
  // Which check's "Ask the customer" form is open in the record (?ask=) — a check's name, nothing of the person's.
  const asking = CHECKS.find((k) => k.key === search.get('ask'))?.key ?? null;
  const opening: Opening = {
    openLead,
    toggleLead: (id) => setParams({ lead: openLead === id ? null : id, tab: null, ask: null }),
  };
  // The search box (the operator, 14 Oct 2026: "how can I search via mobile number/name/TG Id/email?"): what is typed,
  // and — 300 ms after the last key — what is searched for.
  const [typed, setTyped] = useState(initialQuery);
  const searching = typed.trim() !== '';
  const sought = useDebounced(typed.trim(), SEARCH_DEBOUNCE_MS);
  const fromWord = search.get('from') ?? '';
  const from = /^\d{1,6}$/.test(fromWord) ? Number(fromWord) : 0;

  // All three are read whatever the tab, so each tab's count stands on it.
  const q = useQuery({ queryKey: ['onboarding', 'leads'], queryFn: () => onboardingApi.leads(), refetchInterval: READ_EVERY_MS });
  const clientsFrom = view === 'clients' ? from : 0;
  const cq = useQuery({ queryKey: ['onboarding', 'clients', clientsFrom], queryFn: () => onboardingApi.clients(clientsFrom), refetchInterval: READ_EVERY_MS });
  const potentialFrom = view === 'potential' ? from : 0;
  const pq = useQuery({ queryKey: ['onboarding', 'prospects', potentialFrom], queryFn: () => onboardingApi.prospects(potentialFrom), refetchInterval: READ_EVERY_MS });
  // One queue read on past its first page (?queue= ?from=): its own read, with its queue only.
  const chosen = view === 'needs' ? QUEUES.find((s) => s.chip === search.get('queue')) ?? null : null;
  const pageFrom = chosen ? from : 0;
  const later = chosen !== null && pageFrom > 0;
  const paged = useQuery({
    queryKey: ['onboarding', 'leads', chosen?.queue ?? null, pageFrom],
    queryFn: () => onboardingApi.leads({ queue: chosen!.queue, offset: pageFrom }),
    enabled: later && !searching,
    refetchInterval: READ_EVERY_MS,
  });
  // A page read on is a read of its own: its rows' ages are counted to ITS read, and what is wrong with it is said
  // above its rows. A failed read keeps the page it had, said to be the earlier one.
  const pagedRead = readOf(paged, (p) => p.leads.length > 0);
  // The search: a POST, read again each minute while it is on screen, as the lists are. Two characters at least.
  const sq = useQuery({
    queryKey: ['onboarding', 'search', sought],
    queryFn: () => onboardingApi.search(sought),
    enabled: searching && sought.length >= MIN_QUERY,
    refetchInterval: READ_EVERY_MS,
  });
  const leadsRead = readOf(q, (d) => d.leads.length > 0);
  const clientsRead = readOf(cq, (d) => d.clients.length > 0);
  const prospectsRead = readOf(pq, (d) => d.prospects.length > 0);
  const searchRead = readOf(sq, (d) => d.needsYou.length + d.potential.length + d.clients.length + d.elsewhere.length > 0);
  const tracking = (clientsRead.d ? clientsRead.d.contactTrackingSince : null) ?? leadsRead.d?.contactTrackingSince ?? null;
  // The open record, with what its row knows — its standing and the warnings on its card — from every list the screen
  // holds: the search's results (while it searches), then the tab's own rows, then the other tabs' rows; and, when
  // none of them lists the person any more (a search typed on or cleared), the summary last seen for them — a warning
  // never drops off an open record because of what is typed above it. ONE record, in one place at the screen's end: it
  // stays mounted — its focus, its form half filled in — while a search is typed, cleared or a tab's rows read again.
  const found = openLead ? rowSummaryOf(openLead, {
    search: searching ? searchRead : null, view, leads: leadsRead, paged: later ? pagedRead : null, prospects: prospectsRead, clients: clientsRead, tracking,
  }) : null;
  const [lastRow, setLastRow] = useState<{ id: string; row: RowSummary } | null>(null);
  const foundKey = found ? JSON.stringify(found) : '';
  useEffect(() => {
    if (openLead && found) setLastRow((k) => (k && k.id === openLead && JSON.stringify(k.row) === foundKey ? k : { id: openLead, row: found }));
    // `found` is new each render: what it says is foundKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openLead, foundKey]);
  const openRow: RowSummary | null = found ?? (openLead && lastRow?.id === openLead ? lastRow.row : null);
  const active = searching ? searchRead : view === 'clients' ? clientsRead : view === 'potential' ? prospectsRead : leadsRead;
  const activeQ: UseQueryResult<unknown> = searching ? sq : view === 'clients' ? cq : view === 'potential' ? pq : q;
  const needs = needsTotal(leadsRead.d);
  const clients = clientsRead.d && clientsRead.d.counts.approvedHere !== null && clientsRead.d.counts.imported !== null
    ? clientsRead.d.counts.approvedHere + clientsRead.d.counts.imported : null;
  const tabs: Array<{ view: View; label: string; n: number | null; read: boolean; to: Record<string, string | null> }> = [
    { view: 'needs', label: 'Needs you', n: needs, read: !!leadsRead.d, to: { view: null } },
    { view: 'potential', label: 'Potential', n: prospectsRead.d ? prospectsRead.d.total : null, read: !!prospectsRead.d, to: { view: 'potential' } },
    { view: 'clients', label: 'Clients', n: clients, read: !!clientsRead.d, to: { view: 'approved' } },
  ];

  return (
    <div className="space-y-4 px-1 lg:px-0">
      <ScreenHeader
        title="Onboarding"
        subtitle={<NeedsSentence q={q} read={leadsRead} />}
        actions={
          <>
            <span className={`text-meta whitespace-nowrap ${active.d && !active.fresh ? 'text-warn' : 'text-tg-hint'}`}>
              {active.d ? `Updated ${Number.isFinite(active.readMs) ? ago(Math.max(0, Date.now() - active.readMs)) : 'at a time not known'}` : 'Not read yet'}
            </span>
            <button type="button" className="btn-secondary text-body py-1.5 disabled:opacity-50" disabled={activeQ.isFetching}
              onClick={() => qc.invalidateQueries({ queryKey: ['onboarding'] })}>
              {activeQ.isFetching ? 'Reading…' : 'Refresh'}
            </button>
          </>
        }
        help={
          <>
            <p>
              <strong>Needs you</strong>: every lead waiting on the team — stuck (the bot could not send the card),
              waiting for Approve / Reject, approved and not contacted yet, escalated and not marked handled — in the
              onboarding worker&apos;s order. <strong>Potential</strong>: people still in the chat with the bot who said what
              they want, were not refused, and have nothing with the team yet. <strong>Clients</strong>: every client
              approved here, then the verified client records from before.
            </p>
            <p>
              Each person reads as the bot&apos;s four checks — <strong>KYC</strong>, <strong>Residency</strong>,
              <strong> Funds</strong>, <strong>Wallet</strong> — each Passed, Pending or Flagged with one line of why; a cut
              line opens with a tap. Under the trade, what their wallet holds: the coin they sell first, green when it
              covers the sale and red when it is short. The checks are the bot&apos;s reading, never a decision.
            </p>
            <p>
              <strong>Telegram</strong> / <strong>WhatsApp</strong> opens their chat. For a Telegram account with no
              username the bot sends you the button in your chat with it (or posts it in the ops group). A contact a
              website visitor typed in the chat is marked &ldquo;not verified&rdquo;. &ldquo;Website only&rdquo;: a website
              visitor who gave no way to reach them outside the bot; &ldquo;No link to their chat&rdquo;: someone the bot
              knows no link for. <strong>Card</strong> opens the card in the ops group; <strong>Open record</strong> opens
              their whole record beside the list.
            </p>
            <p>
              <strong>Search</strong> finds a person on every tab at once — by name, @username, Telegram id, phone
              number, email or card ref — and says what each was found by; people on no list are found too. What you type
              is searched on the server and never kept. × or Esc brings the tabs back.
            </p>
            <p>
              <strong>Wallet:</strong> what it holds now, read from its chain, with when (&ldquo;3 min ago&rdquo;); when
              the chain did not answer, the last reading and its time. <strong>Waiting on them</strong>: what the bot is
              still asking for, and until when it can write to them — after that it is yours to message them. In a
              record, <strong>Ask the customer</strong> on a check has the bot ask for what is missing; each section
              lists what was asked, who asked and the answer.
            </p>
            <p>
              Every decision is made in the Telegram ops group, on the card: this page decides nothing. Ages are counted
              to the moment the list was read; a list read more than ten minutes ago says so. Records the worker could not
              read are not listed — how many is said, and the counts are then only &ldquo;at least&rdquo;.
            </p>
          </>
        }
      />

      {/* The search, above the tabs: every tab at once. While it holds text its results stand where the tabs were. */}
      <SearchBox typed={typed} setTyped={setTyped} />

      {searching ? (
        <SearchView typed={typed.trim()} sought={sought} q={sq} read={searchRead} tracking={tracking} {...opening} />
      ) : (
        <>
          {/* The tabs: the house's chip row, a count on each. A count that could not be read is "?", never a 0. */}
          <nav className="flex flex-wrap items-center gap-1.5" aria-label="Onboarding views">
            {tabs.map((t) => (
              <button key={t.view} type="button" className={`chip ${view === t.view ? 'chip-on' : ''}`} aria-pressed={view === t.view}
                aria-label={t.read && t.n === null ? `${t.label} — count could not be read` : undefined}
                onClick={() => setParams({ ...t.to, queue: null, from: null, lead: null, tab: null, ask: null })}>
                {t.label}{t.read && <> <span className="font-mono tabular">{t.n === null ? '?' : count(t.n)}</span></>}
              </button>
            ))}
          </nav>

          {view === 'needs'
            ? <NeedsView q={q} read={leadsRead} paged={paged} pagedRead={pagedRead} chosen={chosen} from={pageFrom} later={later} setParams={setParams} {...opening} />
            : view === 'potential'
              ? <PotentialView q={pq} read={prospectsRead} from={potentialFrom} setParams={setParams} {...opening} />
              : <ClientsView q={cq} read={clientsRead} from={clientsFrom} setParams={setParams} trackingFallback={leadsRead.d?.contactTrackingSince ?? null} {...opening} />}
        </>
      )}

      {/* The open record — one, here, whatever stands above it. */}
      {openLead && (
        <RecordSheet id={openLead} row={openRow} tab={recordTab} setTab={(t) => setParams({ tab: t === 'details' ? null : t })}
          asking={asking} setAsking={(k) => setParams({ ask: k })}
          close={() => setParams({ lead: null, tab: null, ask: null })} />
      )}
    </div>
  );
}

// ── The search ───────────────────────────────────────────────────────────────

/** The fewest characters searched for, and how long after the last key. */
const MIN_QUERY = 2;
const SEARCH_DEBOUNCE_MS = 300;
/** The worker reads at most 100 characters: the box takes no more. */
const MAX_QUERY = 100;

/** A value 300 ms after it last changed — a search per pause in the typing, never one per key. Its first value is the
 *  value it starts with. */
function useDebounced<T>(value: T, ms: number): T {
  const [held, setHeld] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setHeld(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return held;
}

/** The search box: one line, its own clear button (×), and Esc to clear — which brings the tabs back. Esc here clears
 *  the search alone: an open record stays open. */
function SearchBox({ typed, setTyped }: { typed: string; setTyped: (s: string) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <div className="relative" role="search">
      <Search size={17} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-tg-hint" aria-hidden />
      <input ref={ref} type="text" inputMode="search" enterKeyHint="search" autoComplete="off" autoCorrect="off" spellCheck={false}
        maxLength={MAX_QUERY} value={typed} onChange={(e) => setTyped(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape' && typed) { e.preventDefault(); e.stopPropagation(); setTyped(''); } }}
        placeholder="Search name, @username, Telegram id, phone, email or card ref" aria-label="Search the onboarding lists"
        className="input w-full text-ellipsis !pl-9 !pr-10" data-search="" />
      {typed && (
        <button type="button" aria-label="Clear the search" title="Clear (Esc)" data-clear=""
          className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-tg-hint hover:bg-tg-secondary hover:text-tg-text"
          onClick={() => { setTyped(''); ref.current?.focus(); }}>
          <X size={16} aria-hidden />
        </button>
      )}
    </div>
  );
}

/** How the worker read what was typed, in words. */
const READ_AS_WORD: Record<ReadAs, string> = {
  name: 'a name or @username', telegram_id: 'a Telegram id', ref: 'a card ref', phone: 'a phone number', email: 'an email',
};
/** Where a person found on no list stands (the brief's words), and its colour: a rejection or a refusal red. */
const STANDING_WORD: Record<NonNullable<ElsewherePerson['standing']>, { text: string; tone: Tone }> = {
  rejected: { text: 'Rejected', tone: 'bad' }, refused: { text: 'Refused by the bot', tone: 'bad' }, blocked: { text: 'Blocked', tone: 'bad' },
  chatting: { text: 'Chatting — no trade named yet', tone: null }, other: { text: 'Not on any list', tone: null },
};

/** The results, where the tabs were: how many were found and how the query was read, then four sections — Needs you,
 *  Potential, Clients, Not on a list — each with its count, each person on the card their tab gives them with what
 *  they were found by. A person on two tabs is on both, as the tabs show them. */
function SearchView({ typed, sought, q, read, tracking, ...o }: {
  typed: string; sought: string; q: UseQueryResult<SearchResult>; read: Read<SearchResult>; tracking: string | null;
} & Opening) {
  const { d, readMs } = read;
  // A key pressed since the last search: what is shown is not yet what is typed.
  const pending = typed !== sought;
  // ONE StateLine per note (design system, 22 Oct 2026): what happened, with the rest of a server's sentence behind Details.
  const head = (text: ReactNode, tone: StateTone = 'neutral', more?: ReactNode) => (
    <div role="status" data-search-note=""><StateLine tone={tone} more={more}>{text}</StateLine></div>
  );
  if (typed.length < MIN_QUERY) return head('Type at least 2 characters to search.');
  if (!d || pending) {
    if (!pending && q.isError) { const f = failSaid("Couldn't search", q.error); return head(f.line, 'warn', f.more); }
    if (!pending && q.fetchStatus === 'paused') return head('No connection — the search runs when it is back.', 'warn');
    return head('Searching…');
  }
  const sections = [
    { key: 'needs', title: 'Needs you', label: 'Found on Needs you',
      people: d.needsYou.map((r) => ({ ...leadPerson(r.lead, readMs, o), found: r.found, key: `n:${r.lead.id ?? r.lead.ref}` })) },
    { key: 'potential', title: 'Potential', label: 'Found on Potential',
      people: d.potential.map((r) => ({ ...prospectPerson(r.prospect, readMs, o), found: r.found, key: `p:${r.prospect.id ?? r.prospect.ref}` })) },
    { key: 'clients', title: 'Clients', label: 'Found on Clients',
      people: d.clients.map((r, i) => ({ ...clientPerson(r.client, i, tracking, readMs, o), found: r.found, key: `c:${r.client.id ?? r.client.customerId ?? i}` })) },
  ];
  const nobody = d.needsYou.length + d.potential.length + d.clients.length + d.elsewhere.length === 0;
  // Found, but sent in a shape this screen cannot show: said, in amber — never "nobody found" (rule 1).
  const lost = d.unreadable > 0 ? d.unreadable : 0;
  const readAs = d.readAs.length ? ` — read as ${d.readAs.map((x) => READ_AS_WORD[x]).join(', ')}` : '';
  // More than the worker lists: the count is of those listed — the first found — and the line under it says there are more.
  const found = d.total === null ? 'People found' : d.more ? `The first ${count(d.total)} found` : d.total === 1 ? '1 person found' : `${count(d.total)} people found`;
  return (
    <section className="space-y-4" aria-label="Search results">
      {q.isError && head(<>Search refresh failed — read {read.readWords}.</>, 'warn', failMore(q.error))}
      {nobody && !lost
        ? <p className="text-body text-tg-text" role="status" data-search-summary="">Nobody found{readAs}.</p>
        : !nobody && <p className="text-body text-tg-text" role="status" data-search-summary="">{found}{readAs}.</p>}
      {lost > 0 && head(nobody
        ? <>{lost === 1 ? '1 match' : `${count(lost)} matches`} found, but not shown.</>
        : <>{lost === 1 ? '1 more match' : `${count(lost)} more matches`} could not be shown.</>, 'warn',
        `The worker sent ${lost === 1 ? 'it' : 'them'} in a shape this screen cannot read${nobody ? readAs : ''}.`)}
      {d.more && <p className="text-body font-medium text-warn" data-search-more="">More than 50 found — type more to narrow it</p>}
      {!nobody && (
        <>
          {sections.map((s) => (
            <div key={s.key} className="space-y-2" data-found-section={s.key}>
              <h2 className={`text-body font-semibold ${s.people.length ? '' : 'text-tg-hint'}`}>
                {s.title} <span className="font-mono tabular text-tg-hint">{count(s.people.length)}</span>
              </h2>
              {s.people.length > 0 && <PersonList label={s.label} people={s.people} />}
            </div>
          ))}
          <div className="space-y-2" data-found-section="elsewhere">
            <h2 className={`text-body font-semibold ${d.elsewhere.length ? '' : 'text-tg-hint'}`}>
              Not on a list <span className="font-mono tabular text-tg-hint">{count(d.elsewhere.length)}</span>
            </h2>
            {d.elsewhere.length > 0 && (
              <ul className="space-y-2" aria-label="Found on no list">
                {d.elsewhere.map((r, i) => <ElsewhereRow key={r.person.id ?? r.person.customerId ?? `e:${i}`} p={r.person} found={r.found} readMs={readMs} o={o} />)}
              </ul>
            )}
          </div>
        </>
      )}
    </section>
  );
}

/** What a person's row says — their standing and the warnings on their card — from the lists the screen holds, the
 *  first that lists them: the search's results (while it searches), the open tab's rows, the other tabs'. PURE. */
function rowSummaryOf(id: string, src: {
  search: Read<SearchResult> | null; view: View; leads: Read<LeadsList>; paged: Read<LeadsList> | null;
  prospects: Read<ProspectsList>; clients: Read<ClientsList>; tracking: string | null;
}): RowSummary | null {
  const fromSearch = () => (src.search?.d ? searchRow(src.search.d, id, src.search.readMs, src.tracking) : null);
  const fromLeads = () => {
    for (const r of [src.paged, src.leads]) {
      const l = r?.d?.leads.find((x) => x.id === id);
      if (l && r) return { standing: standing(l, r.readMs), strip: flagsOf(l) };
    }
    return null;
  };
  const fromProspects = () => {
    const p = src.prospects.d?.prospects.find((x) => x.id === id);
    return p ? { standing: { text: prospectStanding(p, src.prospects.readMs).text, tone: null }, strip: null } : null;
  };
  const fromClients = () => {
    const c = src.clients.d?.clients.find((x) => x.id === id);
    return c ? { standing: clientStanding(c, src.tracking), strip: refusalStrip(c.refusedByBot, src.clients.readMs) } : null;
  };
  const tabs = src.view === 'potential' ? [fromProspects, fromLeads, fromClients]
    : src.view === 'clients' ? [fromClients, fromLeads, fromProspects] : [fromLeads, fromProspects, fromClients];
  for (const f of [fromSearch, ...tabs]) {
    const row = f();
    if (row) return row;
  }
  return null;
}

/** A record opened from the results: its row's standing and warnings, as its tab gives them — or, on no list, where
 *  the person stands. */
function searchRow(d: SearchResult, id: string, readMs: number, tracking: string | null): RowSummary | null {
  const l = d.needsYou.find((r) => r.lead.id === id)?.lead;
  if (l) return { standing: standing(l, readMs), strip: flagsOf(l) };
  const p = d.potential.find((r) => r.prospect.id === id)?.prospect;
  if (p) return { standing: { text: prospectStanding(p, readMs).text, tone: null }, strip: null };
  const c = d.clients.find((r) => r.client.id === id)?.client;
  if (c) return { standing: clientStanding(c, tracking), strip: refusalStrip(c.refusedByBot, readMs) };
  const e = d.elsewhere.find((r) => r.person.id === id)?.person;
  return e?.standing ? { standing: STANDING_WORD[e.standing], strip: null } : null;
}

/** A person found on no list, on one compact line: who, where they stand, when they were last active (to the read),
 *  what they were found by — and their record, when they have a chat here. */
function ElsewhereRow({ p, found, readMs, o }: { p: ElsewherePerson; found: FoundBy[]; readMs: number; o: Opening }) {
  const n = naming(p.name, p.who, p.channel);
  const st = p.standing ? STANDING_WORD[p.standing] : null;
  return (
    <li aria-label={n.name} data-elsewhere={p.standing ?? 'unknown'}>
      <div
        className={`card flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-2.5 ${p.id ? OPENS : ''} ${p.id && o.openLead === p.id ? 'ring-2 ring-tg-link/70' : ''}`}
        onClick={p.id ? cardClick(() => { if (o.openLead !== p.id) o.toggleLead(p.id!); }) : undefined}
        data-opens={p.id ? '' : undefined}
      >
        <span className="min-w-0 break-words text-body font-semibold">{n.name}</span>
        {n.handle && <span className="min-w-0 truncate text-body text-tg-hint" title={n.handle}>{n.handle}</span>}
        {p.channel && <Pill>{CHANNEL_WORD[p.channel]}</Pill>}
        <span className={`text-body ${st?.tone === 'bad' ? 'text-neg' : 'text-tg-text'}`} data-standing="">{st ? st.text : 'Where they stand is not known'}</span>
        {p.lastActiveAt && <span className="text-body text-tg-hint">last active {atRead(p.lastActiveAt, readMs)}</span>}
        <FoundTag found={found} />
        {p.id && <span className="ml-auto"><RecordButton id={p.id} who={n.name} open={o.openLead === p.id} toggle={o.toggleLead} /></span>}
      </div>
    </li>
  );
}

/** Every lead waiting on the team: the four counts added — null when any could not be read (rule 1). */
function needsTotal(d: OnboardingLeads | LeadsList | undefined): number | null {
  if (!d) return null;
  let n = 0;
  for (const s of QUEUES) {
    const c = d.counts[s.count];
    if (c === null) return null;
    n += c;
  }
  return n;
}

/** The header's one sentence: how many people need you, and how long the oldest has waited (to the read). Green only
 *  when it is earned (rule 3); a count not known, or records not read, never read as "nobody". */
function NeedsSentence({ q, read }: { q: UseQueryResult<LeadsList>; read: Read<LeadsList> }) {
  const { d, readMs, fresh, readWords } = read;
  const line = (text: string, tone: 'good' | 'warn' | 'plain') => (
    <span className={`block text-body ${tone === 'good' ? 'text-pos' : tone === 'warn' ? 'text-warn' : 'text-tg-text'}`}>{text}</span>
  );
  if (!d) {
    if (q.isError) return line('Who needs you could not be read.', 'warn');
    if (q.fetchStatus === 'paused') return line('No connection — who needs you reads when it is back.', 'warn');
    return line('Reading who needs you…', 'plain');
  }
  const total = needsTotal(d);
  // Said of an old or failed read: when it was read, so the sentence never passes for now.
  const as = !fresh || q.isError ? ` (as read ${readWords})` : '';
  if (total === null) return line(`Some counts could not be read.${as}`, 'warn');
  if (total === 0) {
    if (d.unreadable !== 0) return line(`Nobody is listed — but records could not be read.${as}`, 'warn');
    return fresh && !q.isError && q.fetchStatus !== 'paused'
      ? line('Nobody needs you right now.', 'good')
      : line(`Nobody needed you when this was read, ${readWords}.`, 'plain');
  }
  // The oldest wait, to the read: the oldest lead listed, or the worker's own oldest decision if it is older.
  let oldest = d.oldestWaitingDays ?? -1;
  for (const l of d.leads) {
    const since = l.since ? Date.parse(l.since) : NaN;
    const days = Number.isFinite(since) && Number.isFinite(readMs) ? Math.floor(Math.max(0, readMs - since) / 86_400_000) : l.ageDays;
    if (days !== null && days > oldest) oldest = days;
  }
  const who = total === 1 ? '1 person needs you' : `${count(total)} people need you`;
  return line(`${who}${oldest >= 0 ? ` — the oldest has waited ${dayWords(oldest)}` : ''}.${as}`, 'warn');
}

/** What is wrong with a list's read, above it: no connection, a failed read (keeping what it had, said to be the
 *  earlier read), a read whose next one waits for a connection, a read more than ten minutes old — and, unless the
 *  header already says it (`reading` false), that the list is being read for the first time. */
function ReadLines({ q, read, noun, reading = true }: { q: UseQueryResult<unknown>; read: Read<unknown>; noun: string; reading?: boolean }) {
  const { d, fresh, readWords } = read;
  // ONE StateLine per read (design system, 22 Oct 2026), in a wrapper the tests read by its marker: what could not be read
  // and — for a list on screen — that it is the earlier read; the rest of the route's sentence behind Details.
  const box = (line: ReactNode, more?: ReactNode, action?: ReactNode) => (
    <div data-box=""><StateLine tone="warn" more={more} action={action}>{line}</StateLine></div>
  );
  const retry = <button type="button" className="btn-link text-meta" onClick={() => void q.refetch()}>Try again</button>;
  if (!d) {
    if (q.isError) { const f = failSaid(`Couldn't read ${noun}`, q.error); return box(f.line, f.more, retry); }
    if (q.fetchStatus === 'paused') return box(<>No connection — {noun} read when it is back.</>);
    return reading ? <p className="text-body text-tg-hint" data-note="">Reading {noun}…</p> : null;
  }
  // A list in hand: the line is what happened and WHEN the list on screen was read. THE STAMP IS FOUR WORDS TODAY ("16:00 · 2 min ago"),
  // FIVE YESTERDAY, SIX BEFORE ("06 Oct, 16:00 · 3 d ago"): each of these leads with four words at most, so the longest still fits ten.
  if (q.isError) return box(<>List refresh failed — read {readWords}.</>, failMore(q.error), retry);
  if (q.fetchStatus === 'paused') return box(<>No connection — list read {readWords}.</>, 'It is read again as soon as the connection is back.');
  if (!fresh) return box(<>Stale list — read {readWords}.</>);
  return null;
}

// ── Needs you ────────────────────────────────────────────────────────────────

/** Opening a record: which one the address names, the button's toggle, and the record itself — given the row's
 *  standing and warnings when its row is on the page. */
type Opening = { openLead: string | null; toggleLead: (id: string) => void };

/** A page read on past the first, as its own read: whether its answer is in hand (`held`), what its read is doing now
 *  (`fetch`), what failed, when it was read and whether that is fresh, and what the worker could not read then. */
type PageHealth = {
  held: boolean; fetch: 'fetching' | 'paused' | 'idle';
  failed: string | null; readWords: string | null; fresh: boolean; unreadable: number | null;
};
type PagerInfo = { start: number; total: number | null; size: number; canPage: boolean; items: string; go: (at: number) => void };

/** How many rows the count says a page holds — the worker's page size, or what is left of the list past where the page
 *  starts — or null when the count could not be read. Only this tells a page whose rows the Books could not show from
 *  a list that has shrunk below where the page starts. */
const pageHolds = (total: number | null, start: number, size: number): number | null =>
  (total === null ? null : Math.max(0, Math.min(size, total - start)));

/** What the chat waits on them for, across a card's foot — amber, with what to do, when the bot can no longer ask. */
function WaitingFoot({ w, channel, readMs }: { w: WaitingOn; channel: LeadChannel | null; readMs: number }) {
  const warn = waitingWords(w, channel, readMs).tone === 'warn';
  return (
    <div className={`border-t px-4 py-2 ${warn ? 'border-warn/25 bg-warn-bg' : 'border-stroke-soft'}`}>
      <WaitingLine w={w} channel={channel} readMs={readMs} />
    </div>
  );
}

/** A lead's card. */
function leadPerson(l: LeadRow, readMs: number, o: Opening): Person {
  const s = standing(l, readMs);
  const n = naming(l.facts.name, l.who, l.channel);
  return {
    key: l.id ?? l.ref,
    ...n,
    channel: l.channel,
    returning: l.labels.some((x) => x.key === 'returning'),
    standing: { ...s, title: l.since ? `Since ${fmtDate(l.since)}` : null },
    note: l.caveats.length > 0
      ? <p className="text-body text-warn" title={l.caveats.join('\n')}>{plural(l.caveats.length, 'caveat', 'caveats')} on the card</p>
      : null,
    chat: <ChatButton t={targetOf(l, { sessionId: l.id }, n.name)} />,
    links: (
      <>
        {l.cardUrl && <CardButton url={l.cardUrl} />}
        {l.id && <RecordButton id={l.id} who={n.name} open={o.openLead === l.id} toggle={o.toggleLead} />}
      </>
    ),
    open: l.id ? () => { if (o.openLead !== l.id) o.toggleLead(l.id!); } : undefined,
    isOpen: Boolean(l.id) && o.openLead === l.id,
    claim: l.claim,
    facts: l.facts,
    wallet: cardWallet(l.walletLive, l.walletCheck, l.claim, noWalletYet(l.assessment), readMs),
    assessment: l.assessment,
    strip: flagsOf(l),
    // A field a Books older than it never sends reads as nothing waiting.
    waiting: l.waitingOn ? <WaitingFoot w={l.waitingOn} channel={l.channel} readMs={readMs} /> : null,
  };
}

/** Needs you in two parts when a lead is ready for a decision: those first, under their own heading, then everyone else
 *  — each part in the worker's order, the latest first (the operator, 14 Oct 2026: "latest first + ready to act"). The
 *  worker's order already puts the ready ones first; the screen names the two parts. Under a queue's own heading the
 *  parts are its sub-headings. */
function NeedsParts({ people, ready, sub }: { people: Person[]; ready: boolean[]; sub: boolean }) {
  const parts = [
    { key: 'ready', title: 'Ready for your decision', label: 'Ready for your decision', people: people.filter((_, i) => ready[i]) },
    { key: 'rest', title: 'Everyone else', label: 'Everyone else who needs you', people: people.filter((_, i) => !ready[i]) },
  ].filter((p) => p.people.length > 0);
  const Head = sub ? 'h3' : 'h2';
  return (
    <>
      {parts.map((p) => (
        <div key={p.key} className="space-y-2" data-needs-part={p.key}>
          <Head className="text-body font-semibold">
            {p.title} <span className="font-mono tabular text-tg-hint">{count(p.people.length)}</span>
          </Head>
          <PersonList label={p.label} people={p.people} />
        </div>
      ))}
    </>
  );
}

/** Every lead waiting on the team. `chosen`, `from`, `later` and the page read on (`paged`, `pagedRead`) are the
 *  screen's: it names the open record's row from them. */
function NeedsView({ q, read, paged, pagedRead, chosen, from, later, setParams, ...o }: {
  q: UseQueryResult<LeadsList>; read: Read<LeadsList>; paged: UseQueryResult<LeadsList>; pagedRead: Read<LeadsList>;
  chosen: QueueInfo | null; from: number; later: boolean; setParams: SetParams;
} & Opening) {
  const { d, readMs } = read;
  const go = (c: Chip | null, at = 0) => setParams({ queue: c, from: c && at > 0 ? String(at) : null, lead: null, tab: null });
  const counted = (s: QueueInfo) => (d ? d.counts[s.count] : null);
  /** A queue's page size, from the worker; a worker older than paging sends none, and cannot read on. */
  const size = d?.page?.size ?? pagedRead.d?.page?.size ?? 100;
  const canPage = !!(d?.page ?? pagedRead.d?.page);
  /** A queue's count: a page read on carries the one read with it — a count it could not read included — and only
   *  with no answer yet is the list's used (an older read's figure would claim what the current answer does not). */
  const totalOf = (s: QueueInfo) => (later && s === chosen ? (pagedRead.d ? pagedRead.d.counts[s.count] : counted(s)) : counted(s));
  const rows: LeadRow[] = later
    ? (pagedRead.d?.leads ?? []).filter((l) => l.queue === chosen!.queue)
    : d ? d.leads.filter((l) => !chosen || l.queue === chosen.queue) : [];
  const rowsReadMs = later ? pagedRead.readMs : readMs;
  // What the list holds against what the counts say it should: a page of each queue, less any lead the Books left
  // out because it could not place it.
  const scope = chosen ? [chosen] : QUEUES;
  const due = scope.every((s) => totalOf(s) !== null)
    ? scope.reduce((n, s) => n + Math.max(0, Math.min((totalOf(s) ?? 0) - (s === chosen ? from : 0), size)), 0)
    : null;
  const pageRead = !later || paged.isSuccess;
  // One queue's page with no row to show says so where its rows would be (EmptyPage); the note is for the rest.
  const mismatch = d && pageRead && due !== null && due !== rows.length && (rows.length > 0 || (!chosen && due > 0))
    ? rows.length < due
      ? `${plural(due - rows.length, "counted lead isn't", "counted leads aren't")} listed — look in the ops group.`
      : `The list holds ${plural(rows.length - due, 'lead', 'leads')} more than the counts say.`
    : null;
  const page: PageHealth | null = later
    ? {
        held: !!pagedRead.d, fetch: paged.fetchStatus, failed: paged.isError ? failWords(paged.error) : null,
        readWords: pagedRead.readWords, fresh: pagedRead.fresh, unreadable: pagedRead.d ? pagedRead.d.unreadable : 0,
      }
    : null;
  const people = rows.map((l) => leadPerson(l, rowsReadMs, o));
  const later1 = from + 1;
  return (
    <>
      <ReadLines q={q} read={read} noun="the leads" reading={false} />
      {/* Records the worker could not build: in no count and no list — a missing record is not "nothing" (rule 1). */}
      {d && d.unreadable === null && (
        <div data-box="">
          <StateLine tone="warn" more="The worker did not say how many records it could not read.">Counts may be higher — unread records not known.</StateLine>
        </div>
      )}
      {d && d.unreadable !== null && d.unreadable > 0 && (
        <div data-box="">
          <StateLine tone="warn" more={`Look for ${d.unreadable === 1 ? 'it' : 'them'} in the ops group.`}>
            Counts may be higher — {plural(d.unreadable, 'record', 'records')} could not be read.
          </StateLine>
        </div>
      )}
      {mismatch && <p className="text-meta text-warn" data-note="">{mismatch}</p>}
      {chosen && (
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <h2 className="font-semibold text-body">{chosen.name}</h2>
          <button type="button" className="btn-link" onClick={() => go(null)}>← Everyone who needs you</button>
        </div>
      )}
      {page && <PageLines page={page} from={later1} holds={rows.length > 0} />}
      {rows.length > 0 ? (
        rows.some((l) => l.ready)
          ? <NeedsParts people={people} ready={rows.map((l) => l.ready)} sub={!!chosen} />
          : <PersonList label="Needs you" people={people} />
      ) : page && !page.held && !page.failed ? (
        // No answer in hand yet: being read — or waiting for a connection, and then never said to be "reading".
        page.fetch === 'paused'
          ? <div data-note=""><StateLine tone="warn">No connection — leads from {later1} read when it&apos;s back.</StateLine></div>
          : <p className="text-body text-tg-hint" data-note="">Reading the leads from {later1}…</p>
      ) : chosen && !page?.failed ? (
        <EmptyPage total={totalOf(chosen)} start={from} size={size} nouns={['lead', 'leads']} fewer="fewer leads in the queue now" none="Nothing in this queue." />
      ) : null}
      {chosen ? (
        <Pager start={from} shown={rows.length} total={totalOf(chosen)} size={size} canPage={canPage} items={chosen.items} go={(at) => go(chosen.chip, at)} />
      ) : (
        // A queue longer than its first page: the rest, a page at a time.
        d && QUEUES.map((s) => {
          const n = counted(s);
          const listed = d.leads.filter((l) => l.queue === s.queue).length;
          return n !== null && n > size && canPage ? (
            <p key={s.queue} className="text-meta text-tg-hint" data-note="">
              {s.name}: the first {count(Math.min(size, listed))} of {count(n)} are listed.{' '}
              <button type="button" className="btn-link" aria-label={`Next ${Math.min(size, n - size)} ${s.items}`} onClick={() => go(s.chip, size)}>
                Next {count(Math.min(size, n - size))} →
              </button>
            </p>
          ) : null;
        })
      )}
    </>
  );
}

/** A page with no row to show, said by the count read with it: rows the count says are there are rows that could not
 *  be shown (in amber); a count at or below where the page starts is a list that has shrunk; a count not known claims
 *  neither. */
function EmptyPage({ total, start, size, nouns, fewer, none }: {
  total: number | null; start: number; size: number; nouns: [string, string]; fewer: string; none: string;
}) {
  const due = pageHolds(total, start, size);
  if (due !== null && due > 0) {
    return <div data-note=""><StateLine tone="warn">{due === 1 ? `The ${nouns[0]} on this page could not be shown.` : `None of the ${count(due)} ${nouns[1]} here could be shown.`}</StateLine></div>;
  }
  if (start > 0) return <div data-note=""><StateLine tone="neutral">{due === 0 ? `Nothing on this page — ${fewer}.` : 'Nothing on this page.'}</StateLine></div>;
  return due === 0
    ? <div data-note=""><EmptyState>{none}</EmptyState></div>
    : <div data-note=""><StateLine tone="neutral">Nothing listed — the count could not be read.</StateLine></div>;
}

/** What is wrong with a page read on, of its own read: it failed (the rows it had stay, said to be the earlier ones),
 *  its next read waits for a connection (the same), it is more than ten minutes old, or the worker could not read some
 *  records when it was read — or did not say. Then, plainly, that a page in hand is being read again. */
function PageLines({ page, from, holds }: { page: PageHealth; from: number; holds: boolean }) {
  const again = page.held && page.fetch === 'fetching';
  const waiting = page.held && page.fetch === 'paused';
  const lines: Array<{ line: ReactNode; more?: ReactNode }> = [];
  if (page.failed) {
    const f = failLine(`Couldn't read leads from ${from}`, page.failed);
    lines.push(holds && page.readWords
      ? { line: <>Page refresh failed — read {page.readWords}.</>, more: sentenceOf(page.failed) }
      : { line: f.line, more: f.more });
  } else if (waiting) {
    lines.push({ line: <>No connection — page read {page.readWords}.</>, more: 'It is read again as soon as the connection is back.' });
  } else if (page.readWords && !page.fresh) {
    lines.push({ line: <>Stale page — read {page.readWords}.</> });
  }
  if (page.readWords && page.unreadable === null) {
    lines.push({ line: <>Page may be missing leads — unread records not known.</>, more: 'The worker did not say how many records it could not read.' });
  } else if (page.readWords && page.unreadable) {
    lines.push({ line: <>Page may be missing leads — {plural(page.unreadable, 'record', 'records')} unread.</>, more: `Look for ${page.unreadable === 1 ? 'it' : 'them'} in the ops group.` });
  }
  return (
    <>
      {lines.length > 0 && (
        <div className="space-y-1" data-page-lines="">
          {lines.map((x, i) => <StateLine key={i} tone="warn" more={x.more}>{x.line}</StateLine>)}
        </div>
      )}
      {again && <p className="text-meta text-tg-hint" data-note="">Reading the leads from {from} again…</p>}
    </>
  );
}

/** Where the rows sit in a list longer than a page — "Showing 101–140 of 140" — and the way to the page before or
 *  after, named for its list. Moving reads another page; it changes nothing. The range is the worker's page, in its
 *  fixed size — not the rows that crossed: a row the Books left out must not shift the next page. A page past the first
 *  always offers the way back; Next is decided by the page and the count, never by the rows that crossed. */
function Pager({ start, shown, total, size, canPage, items, go }: PagerInfo & { shown: number }) {
  const end = total === null ? null : Math.min(start + size, total);
  const missing = end === null ? 0 : Math.max(0, end - start - shown);
  // A count already below where the page starts says no range ("101–90"), only the way back.
  const showing = end !== null && end > start && shown > 0 && (start > 0 || (total ?? 0) > size || missing > 0);
  const before = Math.min(size, start);
  const next = start + size;
  const after = canPage && total !== null && next < total ? Math.min(size, total - next) : 0;
  if (!showing && start === 0 && after === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-meta">
      {showing && (
        <span className="tabular text-tg-hint">
          Showing {count(start + 1)}–{count(end!)} of {count(total!)}{missing > 0 && <> ({count(missing)} could not be shown)</>}
        </span>
      )}
      {start > 0 && (
        <button type="button" className="btn-link" aria-label={`Previous ${before} ${items}`} onClick={() => go(start - before)}>← Previous {count(before)}</button>
      )}
      {after > 0 && (
        <button type="button" className="btn-link" aria-label={`Next ${after} ${items}`} onClick={() => go(next)}>Next {count(after)} →</button>
      )}
    </div>
  );
}

// ── Potential ────────────────────────────────────────────────────────────────

/** A potential lead's standing: when they last wrote, counted to the read — or that they never did — and how many of
 *  the bot's questions they still have to answer, which ones in words. */
function prospectStanding(p: ProspectRow, readMs: number): { text: string; why: string | null } {
  const wrote = p.lastCustomerMessageAt ? `Last wrote ${atRead(p.lastCustomerMessageAt, readMs)}` : p.customerMessages === 0 ? 'Never wrote' : 'In the chat with the bot';
  const togo = p.missing === null ? null : p.missing.length === 0 ? 'every answer given' : `${plural(p.missing.length, 'answer', 'answers')} to go`;
  return { text: [wrote, togo].filter(Boolean).join(' · '), why: p.missing && p.missing.length > 0 ? `Still to answer: ${p.missing.map(missingWord).join(', ')}` : null };
}

/** A potential lead's card. */
function prospectPerson(p: ProspectRow, readMs: number, o: Opening): Person {
  const n = naming(p.name, p.who, p.channel);
  const s = prospectStanding(p, readMs);
  return {
    key: p.id ?? p.ref,
    ...n,
    channel: p.channel,
    standing: { text: s.text, tone: null, why: s.why },
    chat: <ChatButton t={targetOf(p, { sessionId: p.id }, n.name)} />,
    links: p.id && <RecordButton id={p.id} who={n.name} open={o.openLead === p.id} toggle={o.toggleLead} />,
    open: p.id ? () => { if (o.openLead !== p.id) o.toggleLead(p.id!); } : undefined,
    isOpen: Boolean(p.id) && o.openLead === p.id,
    claim: p.claim,
    facts: p.facts,
    wallet: cardWallet(p.walletLive, p.walletCheck, p.claim, noWalletYet(p.assessment, p.missing), readMs),
    assessment: p.assessment,
    strip: null,
    waiting: p.waitingOn ? <WaitingFoot w={p.waitingOn} channel={p.channel} readMs={readMs} /> : null,
  };
}

/** The potential leads (operator, 7 Oct 2026: "instead of all chats, i would like to see potential leads and
 *  KYC'd/Approved customers"): still in the chat with the bot, said what they want, not refused, nothing with the
 *  team yet. The most recently active first, as the worker orders them. */
function PotentialView({ q, read, from, setParams, ...o }: {
  q: UseQueryResult<ProspectsList>; read: Read<ProspectsList>; from: number; setParams: SetParams;
} & Opening) {
  const { d, readMs, fresh, readWords } = read;
  const rows = d ? d.prospects : [];
  const size = d?.page?.size ?? 100;
  const go = (at: number) => setParams({ from: at > 0 ? String(at) : null, lead: null, tab: null });
  const people: Person[] = rows.map((p) => prospectPerson(p, readMs, o));
  return (
    <>
      <ReadLines q={q} read={read} noun="the potential leads" />
      {d && (rows.length > 0 ? (
        <PersonList label="Potential leads" people={people} />
      ) : from === 0 && pageHolds(d.total, 0, size) === 0 ? (
        // Plain, never green: no potential leads is news, not good news.
        fresh && !q.isError
          ? <div data-note=""><EmptyState>No potential leads right now.</EmptyState></div>
          : <div data-note=""><StateLine tone="neutral">No potential leads — read {readWords}.</StateLine></div>
      ) : (
        <EmptyPage total={d.total} start={from} size={size} nouns={['potential lead', 'potential leads']} fewer="fewer potential leads now"
          none="No potential leads right now." />
      ))}
      {(d || from > 0) && <Pager start={from} shown={rows.length} total={d ? d.total : null} size={size} canPage={!!d?.page} items="potential leads" go={go} />}
    </>
  );
}

// ── Clients ──────────────────────────────────────────────────────────────────

/** Whether anyone has contacted an approved client. Contacts are recorded only since the Mark contacted button
 *  (contactTrackingSince, null until its first approval): before it a contact left no record, so a record without one
 *  says nothing either way — "contact not recorded". Only an approval made since can be "not contacted". */
function contactOf(c: ClientRow, trackingSince: string | null): 'contacted' | 'not_recorded' | 'not_contacted' {
  if (c.contacted) return 'contacted';
  if (!trackingSince || !c.approvedAt || Date.parse(c.approvedAt) < Date.parse(trackingSince)) return 'not_recorded';
  return 'not_contacted';
}

/** A client's standing: approved when, and whether anyone has contacted them — amber when nobody has (the approval
 *  message promised "Our team will contact you here shortly"); or a client from before. */
function clientStanding(c: ClientRow, tracking: string | null): { text: string; tone: Tone } {
  if (c.source === 'imported') return { text: `Client from before${c.contacted?.at ? ` · contacted ${fmtDay(c.contacted.at)}` : ''}`, tone: null };
  const when = c.approvedAt ? `Approved ${fmtDay(c.approvedAt)}` : 'Approved (date not known)';
  const how = contactOf(c, tracking);
  if (how === 'contacted') return { text: `${when} · contacted${c.contacted?.at ? ` ${fmtDay(c.contacted.at)}` : ''}`, tone: null };
  return { text: `${when} · ${how === 'not_recorded' ? 'contact not recorded' : 'not contacted'}`, tone: 'warn' };
}

/** Who approved a client and who contacted them, when — the standing's tooltip; an imported record's summary. */
function clientWho(c: ClientRow): string | null {
  if (c.source === 'imported') return c.summary;
  const approved = c.approvedAt ? `Approved ${fmtDate(c.approvedAt)}${c.approvedBy ? ` by ${c.approvedBy}` : ''}` : null;
  const contacted = c.contacted ? `contacted ${c.contacted.at ? fmtDate(c.contacted.at) : 'at a time not known'}${c.contacted.by ? ` by ${c.contacted.by}` : ''}` : null;
  return [approved, contacted].filter(Boolean).join(' · ') || null;
}

/** An approved client's card. `tracking`: when contacts began to be recorded. */
function clientPerson(c: ClientRow, i: number, tracking: string | null, readMs: number, o: Opening): Person {
  const n = naming(c.name, c.who, c.channel);
  return {
    key: c.id ?? c.customerId ?? `${c.ref ?? 'x'}:${i}`,
    ...n,
    channel: c.channel,
    returning: c.source === 'imported',
    standing: { ...clientStanding(c, tracking), title: clientWho(c) },
    chat: <ChatButton t={targetOf(c, { sessionId: c.id, customerId: c.customerId }, n.name)} />,
    links: (
      <>
        {c.cardUrl && <CardButton url={c.cardUrl} />}
        {c.id && <RecordButton id={c.id} who={n.name} open={o.openLead === c.id} toggle={o.toggleLead} />}
        {c.party && (
          <Link to={`/parties/${encodeURIComponent(c.party.slug)}?entity=${c.party.entity}`} className="btn-link whitespace-nowrap text-body" data-party={c.party.slug}
            aria-label={`The party of ${n.name} in the Books: ${c.party.name}`}>
            Party: {c.party.name}
          </Link>
        )}
      </>
    ),
    open: c.id ? () => { if (o.openLead !== c.id) o.toggleLead(c.id!); } : undefined,
    isOpen: Boolean(c.id) && o.openLead === c.id,
    claim: c.claim,
    facts: c.facts,
    wallet: cardWallet(c.walletLive, c.walletCheck, c.claim, noWalletYet(c.assessment), readMs),
    assessment: c.assessment,
    strip: refusalStrip(c.refusedByBot, readMs),
  };
}

/** Every approved client: those approved on a card here, newest first, then the verified records from before.
 *  `trackingFallback`: when contacts began to be recorded, as the leads read says it — for a clients answer without
 *  its own. */
function ClientsView({ q, read, from, setParams, trackingFallback, ...o }: {
  q: UseQueryResult<ClientsList>; read: Read<ClientsList>; from: number; setParams: SetParams; trackingFallback: string | null;
} & Opening) {
  const { d, readMs } = read;
  const rows = d ? d.clients : [];
  const total = d && d.counts.approvedHere !== null && d.counts.imported !== null ? d.counts.approvedHere + d.counts.imported : null;
  const size = d?.page?.size ?? 100;
  const tracking = (d ? d.contactTrackingSince : null) ?? trackingFallback;
  const go = (at: number) => setParams({ from: at > 0 ? String(at) : null, lead: null, tab: null });
  const people: Person[] = rows.map((c, i) => clientPerson(c, i, tracking, readMs, o));
  return (
    <>
      <ReadLines q={q} read={read} noun="the approved clients" />
      {d && (rows.length > 0 ? (
        <PersonList label="Clients" people={people} />
      ) : (
        <EmptyPage total={total} start={from} size={size} nouns={['approved client', 'approved clients']} fewer="fewer approved clients now"
          none="No approved clients yet." />
      ))}
      {(d || from > 0) && <Pager start={from} shown={rows.length} total={total} size={size} canPage={!!d?.page} items="approved clients" go={go} />}
    </>
  );
}
